import {
  SyberryTodayPersonalBacklogCategory,
  SyberryTodayPlanStatus,
  SyberryTodayTaskStatus,
  SyberryTodayWorkItemState,
  type SyberryTodayPlanning,
  type SyberryTodayProject,
  type SyberryTodayTaskGroup,
  type SyberryTodayTaskStream,
  type SyberryTodayWorkItem,
} from '../types';
import {
  SyberryTodayApiCheckinStatus,
  SyberryTodayApiBacklogSectionKey,
  SyberryTodayApiPersonalBacklogCategory,
  type SyberryTodayApiBacklogSection,
  type SyberryTodayApiIssue,
  type SyberryTodayApiProject,
  type SyberryTodayApiQueue,
  type SyberryTodayApiResponse,
} from './types';
import { SYBERRY_TODAY_API_SECTION_KEYS } from './utils';

const UI_CATEGORY_BY_API_CATEGORY: Record<SyberryTodayApiPersonalBacklogCategory, SyberryTodayPersonalBacklogCategory> =
  {
    [SyberryTodayApiPersonalBacklogCategory.INBOX]: SyberryTodayPersonalBacklogCategory.INBOX,
    [SyberryTodayApiPersonalBacklogCategory.BLOCKED]: SyberryTodayPersonalBacklogCategory.BLOCKED,
    [SyberryTodayApiPersonalBacklogCategory.TODAY]: SyberryTodayPersonalBacklogCategory.TODAY,
    [SyberryTodayApiPersonalBacklogCategory.ALL]: SyberryTodayPersonalBacklogCategory.ALL,
    [SyberryTodayApiPersonalBacklogCategory.ONGOING_ACTIVITY]: SyberryTodayPersonalBacklogCategory.ONGOING_ACTIVITY,
    [SyberryTodayApiPersonalBacklogCategory.PARENT_TASKS]: SyberryTodayPersonalBacklogCategory.PARENT_TASKS,
  };

const UNQUEUED_STREAM_NAME_BY_SECTION: Record<SyberryTodayApiBacklogSectionKey, string> = {
  [SyberryTodayApiBacklogSectionKey.TODAY]: 'Today',
  [SyberryTodayApiBacklogSectionKey.LATER]: 'Later',
  [SyberryTodayApiBacklogSectionKey.ONGOING_ACTIVITY]: 'Ongoing activity',
  [SyberryTodayApiBacklogSectionKey.PARENT_TASKS]: 'Parent tasks',
};

const PLAN_STATUS_BY_CHECKIN_STATUS: Record<SyberryTodayApiCheckinStatus, SyberryTodayPlanStatus> = {
  [SyberryTodayApiCheckinStatus.NOT_REQUIRED]: SyberryTodayPlanStatus.CONFIRMED,
  [SyberryTodayApiCheckinStatus.REQUIRED]: SyberryTodayPlanStatus.SELECTING,
  [SyberryTodayApiCheckinStatus.CONFIRMED]: SyberryTodayPlanStatus.CONFIRMED,
  [SyberryTodayApiCheckinStatus.SKIPPED]: SyberryTodayPlanStatus.SKIPPED,
  [SyberryTodayApiCheckinStatus.POSTPONED]: SyberryTodayPlanStatus.POSTPONED,
};

type SyberryTodayApiIssueEntry = {
  issue: SyberryTodayApiIssue;
  project: SyberryTodayApiProject;
  sectionKey: SyberryTodayApiBacklogSectionKey;
  queue?: SyberryTodayApiQueue;
  fallbackPosition: number;
};

const mapCheckinStatusToPlanStatus = (status: SyberryTodayApiCheckinStatus): SyberryTodayPlanStatus =>
  PLAN_STATUS_BY_CHECKIN_STATUS[status];

const getProjectId = (project: SyberryTodayApiProject) => String(project.id);

const getProjectKey = (project: SyberryTodayApiProject) => {
  const normalizedName = project.name.replace(/[^a-zA-Z0-9]/g, '');

  return (normalizedName.slice(0, 3) || `P${project.id}`).toUpperCase();
};

const getIssueKey = (project: SyberryTodayApiProject, issue: SyberryTodayApiIssue) =>
  `${getProjectKey(project)}-${issue.id}`;

const mapIssueStatus = (statusName: string): SyberryTodayTaskStatus => {
  const normalizedStatusName = statusName.trim().toLowerCase();

  if (normalizedStatusName === 'new') return SyberryTodayTaskStatus.NEW;
  if (normalizedStatusName.includes('resolved')) return SyberryTodayTaskStatus.RESOLVED;

  return SyberryTodayTaskStatus.IN_PROGRESS;
};

const getStreamId = (
  project: SyberryTodayApiProject,
  sectionKey: SyberryTodayApiBacklogSectionKey,
  queue?: SyberryTodayApiQueue,
) => {
  if (queue) return String(queue.id);

  return `${project.id}-${sectionKey}`;
};

const getStreamName = (sectionKey: SyberryTodayApiBacklogSectionKey, queue?: SyberryTodayApiQueue) =>
  queue?.name ?? UNQUEUED_STREAM_NAME_BY_SECTION[sectionKey];

const getIssueEntriesFromSection = (
  project: SyberryTodayApiProject,
  section: SyberryTodayApiBacklogSection,
  sectionKey: SyberryTodayApiBacklogSectionKey,
): SyberryTodayApiIssueEntry[] => {
  const queuedEntries = section.queues.flatMap(queue =>
    queue.issues.map((issue, index) => ({
      issue,
      project,
      queue,
      sectionKey,
      fallbackPosition: issue.customQueuePosition ?? index + 1,
    })),
  );
  const unqueuedEntries = section.issues.map((issue, index) => ({
    issue,
    project,
    sectionKey,
    fallbackPosition: issue.personalBacklogPosition ?? index + 1,
  }));

  return [...queuedEntries, ...unqueuedEntries];
};

const getIssueEntriesFromProject = (project: SyberryTodayApiProject): SyberryTodayApiIssueEntry[] =>
  SYBERRY_TODAY_API_SECTION_KEYS.flatMap(sectionKey =>
    getIssueEntriesFromSection(project, project[sectionKey], sectionKey),
  );

const mapApiProject = (project: SyberryTodayApiProject): SyberryTodayProject => ({
  id: getProjectId(project),
  name: project.name,
  key: getProjectKey(project),
});

const mapApiIssueEntry = (entry: SyberryTodayApiIssueEntry): SyberryTodayWorkItem => {
  const { issue, project, queue, sectionKey, fallbackPosition } = entry;
  const status = mapIssueStatus(issue.status.name);
  const selectedForToday = issue.personalBacklogCategory === SyberryTodayApiPersonalBacklogCategory.TODAY;
  const isUnread = issue.personalBacklogCategory === SyberryTodayApiPersonalBacklogCategory.INBOX;

  return {
    id: String(issue.id),
    issueKey: getIssueKey(project, issue),
    title: issue.subject,
    projectId: getProjectId(project),
    projectName: project.name,
    groupId: getProjectId(project),
    streamId: getStreamId(project, sectionKey, queue),
    streamName: getStreamName(sectionKey, queue),
    redmineUrl: issue.redmineUrl,
    state: selectedForToday ? SyberryTodayWorkItemState.DO_TODAY : SyberryTodayWorkItemState.QUEUE,
    selectedForToday,
    todayOrder: selectedForToday ? (issue.personalBacklogPosition ?? fallbackPosition) : undefined,
    personalBacklogPosition: issue.personalBacklogPosition ?? undefined,
    customQueuePosition: issue.customQueuePosition ?? undefined,
    personalBacklogCategory: UI_CATEGORY_BY_API_CATEGORY[issue.personalBacklogCategory],
    isRecurrent: false,
    isUnread,
    assignee: issue.assignee?.name,
    addedBy: issue.author?.name,
    dueDate: issue.dueDate,
    status,
    updatedAt: '',
  };
};

const buildStreams = (projects: SyberryTodayApiProject[]): SyberryTodayTaskStream[] => {
  const streams = new Map<string, SyberryTodayTaskStream>();

  projects.forEach(project => {
    getIssueEntriesFromProject(project).forEach(entry => {
      const streamId = getStreamId(project, entry.sectionKey, entry.queue);

      if (streams.has(streamId)) return;

      streams.set(streamId, {
        id: streamId,
        name: getStreamName(entry.sectionKey, entry.queue),
        projectId: getProjectId(project),
        groupId: getProjectId(project),
      });
    });
  });

  return Array.from(streams.values());
};

const buildTaskGroups = (projects: SyberryTodayApiProject[], tasks: SyberryTodayWorkItem[]): SyberryTodayTaskGroup[] =>
  projects.map(project => {
    const projectId = getProjectId(project);
    const projectTasks = tasks.filter(task => task.groupId === projectId);

    return {
      id: projectId,
      projectId,
      name: project.name,
      totalCount: projectTasks.length,
      unreadCount: projectTasks.filter(task => task.isUnread).length,
    };
  });

export const mapSyberryTodayApiResponseToPlanning = (response: SyberryTodayApiResponse): SyberryTodayPlanning => {
  const tasks = response.projects.flatMap(project => getIssueEntriesFromProject(project).map(mapApiIssueEntry));
  const selectedTaskIds = tasks
    .filter(task => task.selectedForToday)
    .sort((firstTask, secondTask) => (firstTask.todayOrder ?? 0) - (secondTask.todayOrder ?? 0))
    .map(task => task.id);
  const checkinPlanStatus = mapCheckinStatusToPlanStatus(response.checkin.status);

  return {
    projects: response.projects.map(mapApiProject),
    groups: buildTaskGroups(response.projects, tasks),
    streams: buildStreams(response.projects),
    tasks,
    status:
      checkinPlanStatus === SyberryTodayPlanStatus.SELECTING && selectedTaskIds.length
        ? SyberryTodayPlanStatus.ORGANIZING
        : checkinPlanStatus,
    selectedTaskIds,
    systemWarning: response.systemWarning?.trim() ? response.systemWarning : null,
  };
};
